// Layout/TopBar — one 56px bar (Design Standard v1, shell deploy):
//   [ section tabs ……………… ] [ search ] [ tenant ] [ phone ] [ New ] [ you ]
// One global search (TopBarSearch, ⌘K), the tenant switcher, the browser phone's
// state, the one accent-filled action on the shell (New), and the user menu
// (appearance, switch account, log out). On a phone the bar is brand + title +
// search / New / you, and the section tabs drop to a second row.
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, LogOut, Moon, Plus, Repeat, Search, Sun, SlidersHorizontal, X } from 'lucide-react';
import { AuthContext } from '../../App';
import { useTheme } from '../../theme/ThemeProvider';
import { getIndustry } from '../../helpers/tenantHelpers';
import { getIndustryCopy } from '../../constants/industryCopy';
import Button from '../ui/button';
import Menu, { MenuItem, MenuLabel, MenuSeparator } from '../ui/Menu';
import TopBarSearch from './TopBarSearch';
import PhoneStatus from './PhoneStatus';
import { BrandMark } from './Rail';
import './TopBar.css';

const initials = (name = '') => name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() || '').join('') || '?';

// Clearing the active tenant makes /select-account render the chooser instead of
// bouncing back to the dashboard. A HARD navigation on purpose: the picker's guard
// reads the in-memory tenant, which a client-side transition would not clear.
const goToAccountChooser = () => {
  try { localStorage.removeItem('activeTenant'); localStorage.removeItem('activeTenantId'); } catch { /* storage unavailable */ }
  window.location.assign('/select-account');
};

function TenantSwitcher({ compact = false }) {
  const { tenant, tenants = [], selectTenant } = useContext(AuthContext);
  const [busy, setBusy] = useState(false);
  const name = tenant?.name || 'Account';
  const others = tenants.filter((t) => String(t.id) !== String(tenant?.id));
  const pick = async (id) => {
    if (busy) return;
    setBusy(true);
    try {
      await selectTenant(id);
      // Full reload: the voice device, the nav registry and every cached list
      // belong to the tenant that just changed.
      window.location.assign('/dashboard');
    } catch {
      setBusy(false);
      goToAccountChooser();
    }
  };
  if (!others.length) {
    return compact ? null : <span className="shell-chip shell-tenant is-static" title={tenant?.domain || name}><span className="shell-chip__text">{name}</span></span>;
  }
  return (
    <Menu
      className="shell-tenant"
      trigger={(
        <button type="button" className={`shell-chip shell-tenant__btn ${compact ? 'is-compact' : ''}`} aria-label={`Account: ${name}. Switch account`} title={name} disabled={busy}>
          <span className="shell-chip__text">{name}</span>
          <ChevronDown size={14} aria-hidden="true" />
        </button>
      )}
    >
      <MenuLabel>Switch account</MenuLabel>
      {tenants.map((t) => (
        <MenuItem key={t.id} onSelect={() => pick(t.id)} disabled={String(t.id) === String(tenant?.id)} className={String(t.id) === String(tenant?.id) ? 'is-current' : ''}>
          <span className="shell-tenant__name">{t.name}</span>
          {t.domain ? <span className="shell-tenant__meta">{t.domain}</span> : null}
        </MenuItem>
      ))}
      <MenuSeparator />
      <MenuItem icon={<Repeat size={14} />} onSelect={goToAccountChooser}>All accounts…</MenuItem>
    </Menu>
  );
}

function NewMenu({ compact = false }) {
  const navigate = useNavigate();
  const copy = getIndustryCopy(getIndustry('general'));
  return (
    <Menu
      className="shell-new"
      trigger={compact
        ? <Button variant="primary" iconOnly aria-label="New" icon={<Plus size={18} />} />
        : <Button variant="primary" size="sm" icon={<Plus size={16} />}>New</Button>}
    >
      <MenuItem onSelect={() => navigate('/customers/list', { state: { create: 'customer' } })}>Add {copy.customer.toLowerCase()}</MenuItem>
      <MenuItem onSelect={() => navigate('/leads/new', { state: { create: 'lead' } })}>Add {copy.lead.toLowerCase()}</MenuItem>
      <MenuItem onSelect={() => navigate('/schedule/day')}>Book an appointment</MenuItem>
      <MenuItem onSelect={() => navigate('/revival/scanner')}>Scan a quote</MenuItem>
    </Menu>
  );
}

function UserMenu({ phone = false }) {
  const navigate = useNavigate();
  const { user, tenant, tenants = [], logout } = useContext(AuthContext);
  const { resolvedMode, toggleMode } = useTheme();
  const name = user?.name || user?.email || 'You';
  const role = user?.role || '';
  const dark = resolvedMode === 'dark';
  return (
    <Menu
      className="shell-user"
      trigger={(
        <button type="button" className="shell-avatar" aria-label={`${name} — account menu`} title={name}>
          {user?.avatar ? <img src={user.avatar} alt="" /> : <span>{initials(name)}</span>}
        </button>
      )}
    >
      <MenuLabel>
        <span className="shell-user__name">{name}</span>
        {role ? <span className="shell-user__role">{role}</span> : null}
      </MenuLabel>
      {phone ? (
        <>
          <MenuSeparator />
          <MenuLabel>{tenant?.name || 'Account'}</MenuLabel>
          {tenants.length > 1 ? <MenuItem icon={<Repeat size={14} />} onSelect={goToAccountChooser}>Switch account</MenuItem> : null}
        </>
      ) : null}
      <MenuSeparator />
      <MenuItem icon={dark ? <Sun size={14} /> : <Moon size={14} />} onSelect={toggleMode} keepOpen>
        {dark ? 'Light mode' : 'Dark mode'}
      </MenuItem>
      <MenuItem icon={<SlidersHorizontal size={14} />} onSelect={() => navigate('/settings/appearance')}>Appearance</MenuItem>
      {!phone && tenants.length > 1 ? <MenuItem icon={<Repeat size={14} />} onSelect={goToAccountChooser}>Switch account</MenuItem> : null}
      <MenuSeparator />
      <MenuItem danger icon={<LogOut size={14} />} onSelect={() => { logout?.(); navigate('/login'); }}>Log out</MenuItem>
    </Menu>
  );
}

function SectionTabs({ tabs, activeTab, onTabChange }) {
  const ref = useRef(null);
  // Keep the active tab in view when it changes (the strip scrolls on a phone).
  useEffect(() => {
    const el = ref.current?.querySelector('.is-active');
    el?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [activeTab]);
  if (!tabs?.length) return null;
  return (
    <div ref={ref} className="shell-tabs ds-tabs" role="tablist" aria-label="Section">
      {tabs.map((t) => (
        <button key={t.key} type="button" role="tab" aria-selected={activeTab === t.key} className={`ds-tab ${activeTab === t.key ? 'is-active' : ''}`} onClick={() => onTabChange?.(t.key)}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

export default function TopBar({ tabs = [], activeTab, onTabChange, onSearchSelect, sectionLabel = '', phone = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const copy = getIndustryCopy(getIndustry('general'));
  const [searchOpen, setSearchOpen] = useState(false);
  useEffect(() => { setSearchOpen(false); }, [location.pathname]);
  const search = useMemo(() => (
    <TopBarSearch placeholder={copy.searchPlaceholder} onSelect={(row) => { setSearchOpen(false); onSearchSelect?.({ result: row.raw, row }); }} autoFocus={phone && searchOpen} />
  ), [copy.searchPlaceholder, onSearchSelect, phone, searchOpen]);

  if (phone) {
    return (
      <header className={`topbar topbar--phone ${tabs.length ? 'has-tabs' : ''}`}>
        <div className="topbar__row">
          {searchOpen ? (
            <>
              {search}
              <Button variant="ghost" iconOnly aria-label="Close search" icon={<X size={18} />} onClick={() => setSearchOpen(false)} />
            </>
          ) : (
            <>
              <BrandMark size={28} onClick={() => navigate('/dashboard')} className="topbar__brand" />
              <h1 className="topbar__title">{sectionLabel}</h1>
              <div className="topbar__right">
                <Button variant="ghost" iconOnly aria-label="Search" icon={<Search size={18} />} onClick={() => setSearchOpen(true)} />
                <NewMenu compact />
                <UserMenu phone />
              </div>
            </>
          )}
        </div>
        {tabs.length ? <SectionTabs tabs={tabs} activeTab={activeTab} onTabChange={onTabChange} /> : null}
      </header>
    );
  }

  return (
    <header className="topbar">
      <div className="topbar__left">
        <SectionTabs tabs={tabs} activeTab={activeTab} onTabChange={onTabChange} />
      </div>
      <div className="topbar__right">
        {search}
        <TenantSwitcher />
        <PhoneStatus />
        <NewMenu />
        <UserMenu />
      </div>
    </header>
  );
}
